import { useQuery } from "@tanstack/react-query";

import type { Client, DatasetSummary, OverviewSummary } from "../types";
import { useAppQueryClient } from "../lib/queryClient";
import type { FrontPerfTrace } from "../lib/perfAudit";
import { fetchJsonWithPerf, useObserveFrontQuery } from "../lib/perfAudit";

type UseClientDatasetsOptions = {
  enabled?: boolean;
  perfTrace?: FrontPerfTrace | null;
  perfLabel?: string;
};

type FrontQueryPerfOptions = {
  perfTrace?: FrontPerfTrace | null;
  perfLabel?: string;
};

export function useOverview(options?: FrontQueryPerfOptions) {
  const queryClient = useAppQueryClient();
  useObserveFrontQuery(options?.perfTrace, {
    label: options?.perfLabel ?? "query.overview",
    queryKey: ["overview"],
    enabled: true,
  });

  return useQuery<OverviewSummary>(
    {
      queryKey: ["overview"],
      queryFn: async () => {
        const { data } = await fetchJsonWithPerf<OverviewSummary>("/api/overview", undefined, {
          label: options?.perfLabel ?? "query.overview",
          queryKey: ["overview"],
          trace: options?.perfTrace,
        });
        return data;
      },
      staleTime: 60_000,
      placeholderData: (previous) => previous,
    },
    queryClient,
  );
}

export function useClients(options?: FrontQueryPerfOptions) {
  const queryClient = useAppQueryClient();
  useObserveFrontQuery(options?.perfTrace, {
    label: options?.perfLabel ?? "query.clients",
    queryKey: ["clients"],
    enabled: true,
  });

  return useQuery<Client[]>(
    {
      queryKey: ["clients"],
      queryFn: async () => {
        const { data: payload } = await fetchJsonWithPerf<{ clients: Client[] }>("/api/clients", undefined, {
          label: options?.perfLabel ?? "query.clients",
          queryKey: ["clients"],
          trace: options?.perfTrace,
        });
        return payload.clients;
      },
      staleTime: 60_000,
      placeholderData: (previous) => previous,
    },
    queryClient,
  );
}

export function useClientDatasets(clientId?: string, options?: UseClientDatasetsOptions) {
  const queryClient = useAppQueryClient();
  const enabled = Boolean(clientId) && (options?.enabled ?? true);
  useObserveFrontQuery(options?.perfTrace, {
    label: options?.perfLabel ?? "query.client_datasets",
    queryKey: ["clients", clientId, "datasets"],
    enabled,
  });

  return useQuery<DatasetSummary[]>(
    {
      queryKey: ["clients", clientId, "datasets"],
      queryFn: async () => {
        if (!clientId) return [];
        const { data: payload } = await fetchJsonWithPerf<{ datasets: DatasetSummary[] }>(
          `/api/clients/${encodeURIComponent(clientId)}/datasets`,
          undefined,
          {
            label: options?.perfLabel ?? "query.client_datasets",
            queryKey: ["clients", clientId, "datasets"],
            trace: options?.perfTrace,
          },
        );
        return payload.datasets;
      },
      enabled,
      staleTime: 30_000,
      refetchInterval: 30_000,
      placeholderData: (previous) => previous,
    },
    queryClient,
  );
}

export function useDatasets(options?: FrontQueryPerfOptions) {
  const queryClient = useAppQueryClient();
  useObserveFrontQuery(options?.perfTrace, {
    label: options?.perfLabel ?? "query.datasets",
    queryKey: ["datasets"],
    enabled: true,
  });

  return useQuery<DatasetSummary[]>(
    {
      queryKey: ["datasets"],
      queryFn: async () => {
        const { data: payload } = await fetchJsonWithPerf<{ datasets: DatasetSummary[] }>(
          "/api/datasets",
          undefined,
          {
            label: options?.perfLabel ?? "query.datasets",
            queryKey: ["datasets"],
            trace: options?.perfTrace,
          },
        );
        return payload.datasets;
      },
      staleTime: 30_000,
      placeholderData: (previous) => previous,
    },
    queryClient,
  );
}
